import { useCallback, useRef, useState } from 'react';
import { FilterAltIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { Switch } from '@/components/ui/Switch/Switch';
import { PROJECT_FILE_TYPE_VALUES, type ProjectFileTypeValue } from './projectFileTypeConstants';
import { getProjectFileTypeMenuDotColor } from './projectFileTypeBadgeColors';
import type { ProjectFilesPopoverTypeFilterState } from './projectFilesPopoverTypeFilterModel';
import { PROJECT_FILES_TYPE_FILTER_MENU_Z } from './projectFilesPopoverConstants';
import '@/components/ui/ContextMenu/ContextMenu.css';

export interface ProjectFilesPopoverTypeFilterProps {
  state: ProjectFilesPopoverTypeFilterState;
  setTypeToggle: (type: ProjectFileTypeValue, value: boolean) => void;
  setUntypedToggle: (value: boolean) => void;
  isActive: boolean;
}

export function ProjectFilesPopoverTypeFilter({
  state,
  setTypeToggle,
  setUntypedToggle,
  isActive,
}: ProjectFilesPopoverTypeFilterProps) {
  const btnWrapRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((o) => !o), []);

  return (
    <>
      <div ref={btnWrapRef} className="project-files-popover__filter-anchor">
        <IconButton
          aria-label="Фильтр по типу файла"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          title="Фильтр по типу файла"
          size="m"
          iconSize={20}
          variant="default"
          className={`project-files-popover__header-action project-files-popover__filter-btn${
            isActive ? ' project-files-popover__filter-btn_active' : ''
          }`}
          onClick={toggleMenu}
        >
          <FilterAltIcon />
        </IconButton>
      </div>
      <ContextMenu
        isOpen={menuOpen}
        onClose={closeMenu}
        anchorPosition={null}
        anchorEl={menuOpen ? btnWrapRef.current : null}
        anchorPriorityAxis="y"
        excludeRefs={[btnWrapRef]}
        zIndex={PROJECT_FILES_TYPE_FILTER_MENU_Z}
      >
        <ul className="context-menu__list project-files-popover-type-filter-menu" role="menu">
          {PROJECT_FILE_TYPE_VALUES.map((label) => (
            <li key={label} className="context-menu__list-item" role="presentation">
              <div className="context-menu__label switch">
                <Switch
                  checked={state.types[label]}
                  onCheckedChange={(c) => setTypeToggle(label, c)}
                  preventFocusScroll
                  label={
                    <span className="project-files-popover-type-filter-menu__switch-label">
                      <span
                        className="project-files-popover__type-dot"
                        style={{ backgroundColor: getProjectFileTypeMenuDotColor(label) }}
                        aria-hidden
                      />
                      <span>{label}</span>
                    </span>
                  }
                />
              </div>
            </li>
          ))}
          <li className="context-menu__list-item" role="presentation">
            <div className="context-menu__label switch">
              <Switch
                checked={state.untyped}
                onCheckedChange={setUntypedToggle}
                preventFocusScroll
                label={
                  <span className="project-files-popover-type-filter-menu__switch-label">
                    <span className="project-files-popover__type-dot project-files-popover__type-dot_empty" aria-hidden />
                    <span>Без типа</span>
                  </span>
                }
              />
            </div>
          </li>
        </ul>
      </ContextMenu>
    </>
  );
}
